import { Button, CircularProgress, FormHelperText, Grid2, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExpensesSourceRadioGroup, { expenseFileSourceOptions } from './ExpensesSourceRadioGroup';
import ExpensesUploadInput from './ExpensesUploadInput';
import { uploadExpensesFile } from '../../Services/ExpenseService';
import Alert from '../Alert';

const useStyles = makeStyles(() => ({
    PaperRoot: {
        padding: 24,
        display: 'flex',
        minHeight: 300,
        alignItems: 'center',
    },
    form: {
        width: '100%',
    },
}));

const ExpensesUpload = () => {
    const classes = useStyles();
    const methods = useForm();
    const { handleSubmit, errors } = methods;
    const [alert, setAlert] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const flags = useFlags();
    const {
        ltoeWebExpensesEnableNewCreditCardReview: isNewCreditCardReviewEnabled,
        ltoeWebExpensesEnablePreCodedExpenseLogic: isPreCodedExpenseLogicEnabled,
    } = flags;

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            Object.keys(data).forEach((key) => {
                formData.append(key, data[key]);
            });

            await uploadExpensesFile(formData);

            if (data.importer === expenseFileSourceOptions.vault.value) {
                history.push('/expenses/vault/review');
            } else if (isNewCreditCardReviewEnabled) {
                history.push('/expenses/credit-card/review');
            } else if (isPreCodedExpenseLogicEnabled) {
                history.push('/expenses/pending-cc/review');
            } else {
                history.push('/expenses/review');
            }
        } catch (error) {
            const errorAsArray = !!error.errors && Array.isArray(error.errors);
            const errorAsString = typeof error === 'string';
            const getErrorMessage = () => {
                if (errorAsArray)
                    return `Error: ${error.errors.map(({ errorMessage }) => errorMessage)}`;
                if (errorAsString) return error;

                return 'We’re currently experiencing some issues. Please contact the Service Desk.';
            };
            setAlert({
                message: getErrorMessage(),
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Paper classes={{ root: classes.PaperRoot }}>
            {isLoading && <CircularProgress color="primary" />}
            {!isLoading && (
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                        <Grid2 container spacing={4} gap="16px">
                            <Grid2 size={12}>
                                <ExpensesUploadInput />
                                <FormHelperText error>
                                    {errors && errors.file && errors.file.message}
                                </FormHelperText>
                            </Grid2>
                            <Grid2 size={12}>
                                <ExpensesSourceRadioGroup />
                            </Grid2>
                            <Grid2 size={12}>
                                <Button type="submit" variant="contained" color="primary">
                                    Upload
                                </Button>
                            </Grid2>
                        </Grid2>
                    </form>
                </FormProvider>
            )}
            <Alert alert={alert} setAlert={setAlert} />
        </Paper>
    );
};

export default ExpensesUpload;
